import type { CountryIso3166 } from "@tfg/types/constants/country.constants"

import type {
  CreateProductionSiteMutationRequest,
  UpdateProductionSiteMutationRequest,
} from "~/gen/api/types"
import type { CreateProductionSiteResponseDto } from "~/gen/api/types/CreateProductionSiteResponseDto"
import type { FindProductionSitesForSupplierResponseDto } from "~/gen/api/types/FindProductionSitesForSupplierResponseDto"
import type { ProductionSiteFormValues } from "~/pages/SupplierPortal/ProductionSites/schemas/productionSiteForm.schema"
import {
  type BusinessRelationshipOption,
  resolveInitialParentCompanyValue,
  toParentCompanyLink,
} from "../utils/productionSiteParentCompany.utils"

export type CreateProductionSiteResultKind = CreateProductionSiteResponseDto["kind"]

export type CreatedProductionSiteSnapshot = Pick<
  FindProductionSitesForSupplierResponseDto,
  "id" | "name" | "country" | "street" | "city" | "postalCode"
>

interface ProductionSiteInitialValues
  extends Omit<ProductionSiteFormValues, "countryIso3166"> {
  readonly countryIso3166: CountryIso3166 | null
}

type CreateProductionSiteMutationPayload = {
  readonly kind?: CreateProductionSiteResponseDto["kind"]
  readonly id?: string
  readonly productionSite?: {
    readonly id?: string
  }
}

const DEFAULT_INITIAL_VALUES: ProductionSiteInitialValues = {
  repositoryRecordId: undefined,
  name: "",
  parentCompany: "",
  internalCode: "",
  performedActivities: [],
  registrationIds: [],
  street: "",
  city: "",
  postalCode: "",
  longitude: undefined,
  latitude: undefined,
  countryIso3166: null,
}

const isCreateProductionSiteResultKind = (
  value: unknown
): value is CreateProductionSiteResultKind => {
  return value === "created" || value === "merged"
}

export const parseCreateProductionSiteMutationPayload = (
  value: unknown
): CreateProductionSiteMutationPayload | null => {
  if (typeof value !== "object" || value === null) {
    return null
  }

  const kindRaw = Reflect.get(value, "kind")
  const kind = isCreateProductionSiteResultKind(kindRaw) ? kindRaw : undefined

  const productionSiteRaw = Reflect.get(value, "productionSite")
  const productionSiteIdRaw =
    typeof productionSiteRaw === "object" && productionSiteRaw !== null
      ? Reflect.get(productionSiteRaw, "id")
      : undefined

  const topLevelIdRaw = Reflect.get(value, "id")

  return {
    kind,
    id: typeof topLevelIdRaw === "string" ? topLevelIdRaw : undefined,
    productionSite:
      typeof productionSiteIdRaw === "string"
        ? { id: productionSiteIdRaw }
        : undefined,
  }
}

export const getCreateModeInitialValues = (): Record<string, unknown> => {
  return {
    ...DEFAULT_INITIAL_VALUES,
  }
}

export const getEditModeInitialValues = (
  productionSite: FindProductionSitesForSupplierResponseDto,
  canEditParentCompany: boolean,
  businessRelationshipOptions: ReadonlyArray<BusinessRelationshipOption>
): Record<string, unknown> => {
  return {
    repositoryRecordId: undefined,
    name: productionSite.name,
    parentCompany: resolveInitialParentCompanyValue(
      canEditParentCompany,
      toParentCompanyLink(productionSite.parentCompany),
      businessRelationshipOptions
    ),
    internalCode: productionSite.internalCode ?? "",
    performedActivities: productionSite.performedActivities?.map((a) => a.id) ?? [],
    registrationIds:
      productionSite.registrationIds?.map((registrationId) => ({
        id: registrationId.id,
        registrationId: registrationId.registrationId,
        type: registrationId.type,
      })) ?? [],
    street: productionSite.street ?? "",
    city: productionSite.city ?? "",
    postalCode: productionSite.postalCode ?? "",
    longitude: productionSite.longitude ?? undefined,
    latitude: productionSite.latitude ?? undefined,
    countryIso3166: productionSite.country ?? null,
  }
}

export const getCreatePayloadFromFormValues = (
  values: ProductionSiteFormValues
): { readonly data: CreateProductionSiteMutationRequest } => {
  return {
    data: {
      ...(values.repositoryRecordId !== undefined
        ? { repositoryRecordId: values.repositoryRecordId }
        : {}),
      name: values.name,
      street: values.street,
      city: values.city,
      postalCode: values.postalCode,
      longitude: values.longitude,
      latitude: values.latitude,
      countryIso3166: values.countryIso3166,
      internalCode: values.internalCode ?? "",
      performedActivities: values.performedActivities,
      registrationIds: values.registrationIds ?? [],
      ownerOrganizationId: values.parentCompany,
    },
  }
}

export const getUpdatePayloadFromFormValues = (
  values: ProductionSiteFormValues
): { readonly data: UpdateProductionSiteMutationRequest } => {
  return {
    data: {
      name: values.name,
      street: values.street,
      city: values.city,
      postalCode: values.postalCode,
      longitude: values.longitude,
      latitude: values.latitude,
      countryIso3166: values.countryIso3166,
      internalCode: values.internalCode,
      performedActivities: values.performedActivities,
      registrationIds: values.registrationIds,
    },
  }
}

export const toCreatedProductionSiteSnapshot = (
  productionSiteId: string,
  values: ProductionSiteFormValues
): CreatedProductionSiteSnapshot => {
  return {
    id: productionSiteId,
    name: values.name,
    country: values.countryIso3166,
    street: values.street,
    city: values.city,
    postalCode: values.postalCode ?? null,
  }
}
